import { useEffect } from 'react';
import type { AbilityDemo } from './types';

export function SampleModal({
  demo,
  onClose,
}: {
  demo: AbilityDemo;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-ink-950/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-labelledby="sample-modal-title"
        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg-ornate border border-gold-400/40 bg-gradient-to-br from-ink-700/95 to-ink-900/95 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-4 flex flex-wrap items-baseline justify-between gap-2 border-b border-gold-400/20 pb-3">
          <div>
            <div className="font-display text-[11px] uppercase tracking-[0.28em] text-gold-400">
              Sample scenes
            </div>
            <h3 id="sample-modal-title" className="font-serif text-xl text-parchment-50">
              {demo.label}
            </h3>
          </div>
          <div className="font-mono text-[13px] text-gold-300">{demo.roll.formula}</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto font-display text-xs uppercase tracking-[0.22em] text-parchment-100/60 hover:text-gold-300"
          >
            Close ✕
          </button>
        </header>

        {demo.samples && demo.samples.length > 0 ? (
          <ol className="space-y-3 text-sm text-parchment-100/85">
            {demo.samples.map((s, i) => (
              <li key={i} className="flex gap-3 border-b border-gold-400/10 pb-3 last:border-0">
                <span className="font-display text-[11px] uppercase tracking-[0.22em] text-gold-400 pt-0.5">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="flex-1 whitespace-pre-line leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="muted text-sm italic">No sample scenes written yet.</p>
        )}
      </div>
    </div>
  );
}
